import { Container } from '@pixi/react'
import { ComponentProps, useEffect, useRef } from 'react'
import { Assets } from 'pixi.js'
import { IContainer } from '../type/PixiType'
import { AnimationStateListener, Event, Spine, TrackEntry } from '@esotericsoftware/spine-pixi'

type ContainerProps = ComponentProps<typeof Container>
type EventMode = ContainerProps['eventMode']

export interface ISpinePlayerProps {
  data: string
  atlas: string
  animationStr?: string
  skin?: string
  loop?: boolean
  x: number
  y: number
  width?: number
  height?: number
  scale?: number
  playOnLoad?: boolean
  timescale?: number
  onCompletedCallback?: () => void
  onCreatedSpine?: (spine: Spine) => void
  onEventCallback?: (trackEntry: TrackEntry, event: Event) => void
  onStartCallback?: (trackEntry: TrackEntry) => void
  eventMode?: EventMode
  name?: string
  zIndex?: number
}

export const SpinePlayer = ({
  data,
  atlas,
  animationStr = undefined,
  skin = undefined,
  loop = false,
  x,
  y,
  width,
  height,
  scale = 1,
  timescale = 1,
  onCompletedCallback,
  onCreatedSpine,
  playOnLoad = true,
  onEventCallback,
  onStartCallback,
  eventMode = 'auto',
  name,
  zIndex = 0,
}: ISpinePlayerProps) => {
  const containerRef = useRef<IContainer>(null)
  const urlRef = useRef<string>()
  const spineRef = useRef<Spine>()
  const completeFuncRef = useRef<AnimationStateListener>()
  const eventFuncRef = useRef<AnimationStateListener>()
  const onCreatedSpineRef = useRef(onCreatedSpine)

  useEffect(() => {
    (async function loadSpine() {
      const container = containerRef.current
      if (container === null) return
      let spine: Spine | null = null
      if (urlRef.current !== data) {
        await Assets.load([data, atlas])

        spine = Spine.from(data, atlas)
        spine.x = 0
        spine.y = 0

        spineRef.current = spine
        urlRef.current = data

        if (container.children.length > 0) {
          container.removeChildAt(0)
        }
        spine.eventMode = eventMode
        spine.state.timeScale = timescale
        container.addChild(spine)
        onCreatedSpineRef.current?.(spine)
      }
      else if (spineRef.current !== undefined) {
        spine = spineRef.current
      }

      if (spine !== null) {
        if (onCompletedCallback) {
          completeFuncRef.current = {
            complete: () => {
              onCompletedCallback()
              spine.state.removeListener(completeFuncRef.current as AnimationStateListener)
            },
          }
          spine.state.addListener(completeFuncRef.current)
        }

        if (onStartCallback) {
          completeFuncRef.current = {
            start: (trackEntry) => {
              onStartCallback(trackEntry)
              spine.state.removeListener(completeFuncRef.current as AnimationStateListener)
            },
          }
          spine.state.addListener(completeFuncRef.current)
        }

        if (onEventCallback) {
          eventFuncRef.current = {
            event: (entry, event) => {
              onEventCallback(entry, event)
            },
          }
          spine.state.addListener(eventFuncRef.current)
        }
        if (playOnLoad && animationStr !== undefined) spine.state.setAnimation(0, animationStr, loop)

        if (skin) {
          spine.skeleton.setSkinByName(skin)
        }
      }
    })()

    return () => {
      if (spineRef.current) {
        if (eventFuncRef.current) {
          spineRef.current.state.removeListener(eventFuncRef.current)
        }
        if (completeFuncRef.current) {
          spineRef.current.state.removeListener(completeFuncRef.current)
        }
      }
    }
  }, [
    animationStr,
    loop,
    data,
    atlas,
    onCompletedCallback,
    playOnLoad,
    onEventCallback,
    eventMode,
    timescale,
    onStartCallback,
    skin,
  ])

  useEffect(() => {
    if (spineRef.current) {
      const spine = spineRef.current
      if (width !== undefined) {
        spine.width = width
      }
      if (height !== undefined) {
        spine.height = height
      }
    }
  }, [height, width])

  return <Container x={x} y={y} scale={scale} ref={containerRef} eventMode={eventMode} name={`SpinePlayer${name ? '-' + name : ''}`} zIndex={zIndex} />
}
